/**
 * Modal — envoyer un broadcast urgent (admin/direction uniquement).
 * Le message apparaît en pop-up chez tous les destinataires.
 */
import { useState } from 'react';
import { X, AlertTriangle, Send } from 'lucide-react';
import { useBroadcastUrgentMutation } from '../../services/messagerieChat';

const CIBLES = [
  { k: 'tous', l: 'Tous les agents' },
  { k: 'agent', l: 'Scolarité' },
  { k: 'formateur', l: 'Formateurs' },
  { k: 'surveillant', l: 'Surveillance' },
  { k: 'directeur', l: 'Direction' },
];

export function BroadcastAdminModal({ onClose }: { onClose: () => void }) {
  const [contenu, setContenu] = useState('');
  const [niveau, setNiveau] = useState<'info' | 'important' | 'urgent'>('important');
  const [cible, setCible] = useState('tous');
  const [broadcast, { isLoading }] = useBroadcastUrgentMutation();

  const handleSend = async () => {
    if (!contenu.trim()) return;
    if (!window.confirm(`Envoyer ce broadcast ${niveau.toUpperCase()} à "${CIBLES.find(c=>c.k===cible)?.l}" ?`)) return;
    try {
      const r = await broadcast({
        contenu: contenu.trim(),
        niveau,
        cibles: cible === 'tous' ? 'tous' : [cible],
      }).unwrap();
      alert(`Broadcast envoyé à ${r.nb_destinataires} destinataire(s).`);
      onClose();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-950 rounded-lg shadow-xl w-full max-w-lg">
        <div className="p-3 border-b border-slate-200 dark:border-gray-800 flex items-center justify-between">
          <h2 className="font-bold text-red-600 flex items-center gap-2">
            <AlertTriangle size={16} />
            Broadcast urgent
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-gray-800 rounded">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Niveau</label>
            <div className="flex gap-2">
              {(['info', 'important', 'urgent'] as const).map(n => (
                <button key={n} onClick={() => setNiveau(n)}
                  className={`flex-1 py-2 text-xs rounded font-semibold ${
                    niveau === n
                      ? n === 'urgent' ? 'bg-red-600 text-white'
                        : n === 'important' ? 'bg-amber-500 text-white'
                        : 'bg-blue-500 text-white'
                      : 'bg-slate-100 dark:bg-gray-900 text-slate-600 dark:text-gray-400'
                  }`}>
                  {n.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Destinataires</label>
            <select value={cible} onChange={(e) => setCible(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-slate-900 dark:text-white">
              {CIBLES.map(c => <option key={c.k} value={c.k}>{c.l}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">
              Message ({contenu.length}/500)
            </label>
            <textarea value={contenu} onChange={(e) => setContenu(e.target.value)}
              maxLength={500} rows={4} autoFocus
              placeholder="Message clair et concis..."
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-slate-900 dark:text-white resize-none" />
          </div>

          <div className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
            ⚠ Ce message sera envoyé à tous les destinataires avec notification push et apparaîtra en pop-up chez ceux qui sont en ligne.
          </div>
        </div>

        <div className="p-3 border-t border-slate-200 dark:border-gray-800 flex justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-1.5 text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800 rounded">
            Annuler
          </button>
          <button onClick={handleSend} disabled={isLoading || !contenu.trim()}
            className={`px-4 py-1.5 text-sm text-white font-semibold rounded flex items-center gap-2 ${
              niveau === 'urgent' ? 'bg-red-600 hover:bg-red-700' :
              niveau === 'important' ? 'bg-amber-500 hover:bg-amber-600' :
              'bg-blue-500 hover:bg-blue-600'
            } disabled:bg-slate-300 disabled:cursor-not-allowed`}>
            <Send size={13} />
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}
